// The Frontend's errors, reported to the fullstack's Monitor (Seán, 2026-10-29; the fullstack's
// claude/ERRORS.md § The Frontend): a port of its browser capture
// (base-layer/app/composables/useClientErrorCapture.js) to plain Vue. Uncaught errors, unhandled
// rejections, Vue errors and console.error; repeats within a page load merged into one report
// with a count, at most 20 reports a page load. Posted to the matching fullstack (localhost's,
// staging's or production's: VUE_APP_FULLSTACK) as site "frontend"; it masks, groups and
// files them. Never throws, never logs its own failures, ignores what's raised while reporting.
import {getFullstackUrl} from "@/composables/useFullstackUrl";

const ENDPOINT = `${getFullstackUrl()}/api/v1/errors/client`;
const FLUSH_DELAY_MS = 2000;
const MAX_REPORTS_PER_PAGE_LOAD = 20;
const MAX_REPORTS_PER_REQUEST = 10; // the endpoint's limit
const BROWSER_ID_KEY = "error-reporter-browser-id";
const CODE_VERSION = process.env.VUE_APP_COMMIT || "unknown"; // vue.config.js
const pending = new Map();
let sentCount = 0;
let flushTimer = null;
let isReporting = false;

// a random id per browser for signed-out visitors: counts distinct browsers, names no one
function getBrowserId() {
  try {
    const existing = localStorage.getItem(BROWSER_ID_KEY);
    if (existing) return existing;
    const created = crypto.randomUUID();
    localStorage.setItem(BROWSER_ID_KEY, created);
    return created;
  } catch {
    return null;
  }
}

// console arguments become strings: objects only by their type, never serialised
function describeValue(value) {
  if (value instanceof Error) return value.message;
  if (typeof value === "string") return value;
  if (value === null || value === undefined) return String(value);
  if (typeof value === "object") return `[${value.constructor?.name || "Object"}]`;
  return String(value);
}

function describeError(error) {
  if (!(error instanceof Error)) {
    return {name: typeof error, message: describeValue(error), stack: ""};
  }
  return {name: error.name, message: error.message, stack: error.stack};
}

function describeConsoleArguments(values) {
  const error = values.find((value) => value instanceof Error);
  const message = values.map(describeValue).join(" ");
  return {name: error?.name || "console.error", message, stack: error?.stack || ""};
}

async function getSessionToken() {
  try {
    return (await window.Clerk?.session?.getToken()) ?? null;
  } catch {
    return null;
  }
}

// signed in: the session token names the account; leaving the page: a plain request (keepalive,
// no preflight), so the last batch still goes, without the account
async function sendBatch(batch, isLeaving) {
  const body = JSON.stringify({browserId: getBrowserId(), site: "frontend", reports: batch});
  if (isLeaving) {
    const headers = {"Content-Type": "text/plain"};
    return fetch(ENDPOINT, {method: "POST", body, headers, keepalive: true}).catch(() => {});
  }
  const token = await getSessionToken();
  const headers = {"Content-Type": "application/json"};
  if (token) headers.Authorization = `Bearer ${token}`;
  await fetch(ENDPOINT, {method: "POST", body, headers}).catch(() => {});
}

function flush(isLeaving = false) {
  clearTimeout(flushTimer);
  flushTimer = null;
  const reports = [...pending.values()].slice(0, MAX_REPORTS_PER_PAGE_LOAD - sentCount);
  pending.clear();
  if (!reports.length) return;
  sentCount += reports.length;
  for (let start = 0; start < reports.length; start += MAX_REPORTS_PER_REQUEST) {
    sendBatch(reports.slice(start, start + MAX_REPORTS_PER_REQUEST), isLeaving).catch(() => {});
  }
}

// the page being left: a request it had under way fails ("Failed to fetch"), not a fault
let isLeaving = false;
const NETWORK_FAILURE = /Failed to fetch|NetworkError|Load failed|The operation was aborted/;
// the browser's, not ours (2026-10-29): a ResizeObserver warning; OpenSeadragon cancelling a
// poster's tiles no longer needed as the visitor zooms, pans or leaves (the tiles exist)
const BROWSER_NOISE = /^ResizeObserver loop|Tile .* failed to load.*Image load aborted/s;
// requests that got no answer at all, never a fault of the page (2026-10-30, all from crawlers
// that block scripts: the same pages load cleanly in a browser): Clerk's own scripts; a poster's
// tile description (HTTP 0: no response; a missing file is HTTP 404, still recorded); the
// Profile's video catalogue, which falls back to none
const NOT_LOADED = new RegExp([
  "code=\"failed_to_load_clerk_(js|ui)\"",
  "^HTTP 0 attempting to load TileSource",
  "^useLockedVideos: TypeError: (Failed to fetch|NetworkError|Load failed)"
].join("|"));

// not errors of ours: the dev server's own messages; Clerk's lazily loaded scripts failing to
// fetch themselves; a request cut off as the page is left (2026-10-29: "Failed to fetch" as a
// visitor or a robot closed the home page while it loaded)
function isNotRecorded(described) {
  const message = String(described.message || "");
  if (message.startsWith("[webpack-dev-server]") || message.startsWith("[HMR]")) return true;
  if (isLeaving && NETWORK_FAILURE.test(message)) return true;
  if (BROWSER_NOISE.test(message) || NOT_LOADED.test(message)) return true;
  return described.name === "ChunkLoadError" && message.includes("/npm/@clerk/");
}

function queueReport(source, described) {
  if (isNotRecorded(described)) return;
  const key = `${source}|${described.name}|${described.message}`.slice(0, 500);
  const existing = pending.get(key);
  if (existing) {
    existing.repeatCount += 1;
    return;
  }
  if (sentCount + pending.size >= MAX_REPORTS_PER_PAGE_LOAD) return;
  const route = window.location.pathname; // the path only: a query or hash can carry tokens
  pending.set(key, {source, ...described, route, codeVersion: CODE_VERSION, repeatCount: 1});
  if (!flushTimer) flushTimer = setTimeout(flush, FLUSH_DELAY_MS);
}

function capture(source, describe) {
  if (isReporting) return;
  isReporting = true;
  try {
    queueReport(source, describe());
  } catch {
    // capture never breaks the page
  } finally {
    isReporting = false;
  }
}

// another site's script loaded without CORS reports only "Script error.": nothing to act on
const isMutedCrossOriginError = (event) =>
  !event.error && /^Script error\.?$/.test(String(event.message || ""));

function wrapConsoleError() {
  const originalConsoleError = console.error.bind(console);
  console.error = (...values) => {
    originalConsoleError(...values);
    capture("console-error", () => describeConsoleArguments(values));
  };
}

// Vue's handler: the error still logged (Vue's default), then reported once, as a Vue error
function handleVueErrors(app) {
  const previous = app.config.errorHandler;
  app.config.errorHandler = (error, instance, info) => {
    capture("vue-error", () => describeError(error));
    if (previous) return previous(error, instance, info);
    isReporting = true; // its own console.error line isn't a second report
    console.error(error);
    isReporting = false;
  };
}

// an automated browser (Vercel's deploy screenshots, crawlers, our tests: Seán, 2026-10-29): not
// reported; tests watch their own errors (fullstack tests/e2e/helpers/errorWatch.js)
const getIsAutomated = () => Boolean(navigator.webdriver)
  || /HeadlessChrome/.test(navigator.userAgent || "");

export default {
  install(app) {
    if (getIsAutomated()) return;
    window.addEventListener("beforeunload", () => (isLeaving = true));
    window.addEventListener("error", (event) => {
      if (isMutedCrossOriginError(event)) return;
      capture("window-error", () => describeError(event.error ?? event.message));
    });
    window.addEventListener("unhandledrejection", (event) => {
      capture("unhandled-rejection", () => describeError(event.reason));
    });
    handleVueErrors(app);
    wrapConsoleError();
    window.addEventListener("pagehide", () => {
      isLeaving = true;
      flush(true);
    });
  },
};
